/**
 * App Configuration Composable
 * Replaces inline scripts in base.html for APP_CONFIG, APP_SESSION
 * 
 * This module reads configuration from window.APP_SETTINGS injected by Flask
 * and provides a clean Vue composable interface.
 */

import { computed, readonly, ref } from 'vue';

interface CustomerData {
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
}

interface AppSession {
  customer: CustomerData | null;
}

// Extend Window interface - uses declaration from types/global.d.ts
// This file just provides the composable implementation

interface AppConfig {
  app_name: string;
  restaurant_name: string;
  restaurant_assets: string;
  static_host_url: string;
  current_year: string;
  debug_mode: boolean;
  server_time: string;
  currency_code: string;
  currency_symbol: string;
  tax_rate: number;
  'payments.enable_cashier_role'?: boolean;
  'payments.allow_waiter_cashier_operations'?: boolean;
}

// Reactive state
const isInitialized = ref(false);
const config = ref<AppConfig>({
  app_name: 'Enfa',
  restaurant_name: 'Restaurante',
  restaurant_assets: '/assets/enfa',
  static_host_url: '',
  current_year: new Date().getFullYear().toString(),
  debug_mode: false,
  server_time: new Date().toISOString(),
  currency_code: 'MXN',
  currency_symbol: '$',
  tax_rate: 0.16,
});

const session = ref<AppSession>({
  customer: null,
});

const apiBase = ref('');

function requireObject(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} inválido o ausente`);
  }
  return value as Record<string, unknown>;
}

function requireStringField(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`APP_SETTINGS.${key} es obligatorio`);
  }
  return value;
}

function requireBooleanField(source: Record<string, unknown>, key: string): boolean {
  const value = source[key];
  if (typeof value !== 'boolean') {
    throw new Error(`APP_SETTINGS.${key} debe ser boolean`);
  }
  return value;
}

function readBooleanField(
  source: Record<string, unknown>,
  key: string,
  defaultValue: boolean
): boolean {
  const value = source[key];
  if (value === undefined || value === null) return defaultValue;
  if (typeof value !== 'boolean') {
    throw new Error(`APP_SETTINGS.${key} debe ser boolean`);
  }
  return value;
}

function requireNumberField(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new Error(`APP_SETTINGS.${key} debe ser numérico`);
  }
  return value;
}

/**
 * Initialize app configuration from window globals
 * Called once on app startup
 */
export function initAppConfig(): void {
  if (isInitialized.value) return;

  // Read APP_SETTINGS from Flask template
  const settings = requireObject(window.APP_SETTINGS, 'APP_SETTINGS');
  const allowWaiterCashierOperations = readBooleanField(
    settings,
    'payments.allow_waiter_cashier_operations',
    true
  );
  const enableCashierRole = readBooleanField(
    settings,
    'payments.enable_cashier_role',
    true
  );

  // Build immutable config
  config.value = {
    app_name: requireStringField(settings, 'app_name'),
    restaurant_name: requireStringField(settings, 'restaurant_name'),
    restaurant_assets: requireStringField(settings, 'restaurant_assets'),
    static_host_url: requireStringField(settings, 'static_host_url'),
    current_year: requireStringField(settings, 'current_year'),
    debug_mode: requireBooleanField(settings, 'debug_mode'),
    server_time: requireStringField(settings, 'server_time'),
    currency_code: requireStringField(settings, 'currency_code'),
    currency_symbol: requireStringField(settings, 'currency_symbol'),
    tax_rate: requireNumberField(settings, 'tax_rate'),
    'payments.enable_cashier_role': enableCashierRole,
    'payments.allow_waiter_cashier_operations': allowWaiterCashierOperations,
  };

  // Read session data. Anonymous guests do not always have server-provided
  // customer data yet, so the client shell must boot with a null customer.
  const sessionData = window.APP_SESSION
    ? requireObject(window.APP_SESSION, 'APP_SESSION')
    : { customer: null };
  session.value = { customer: (sessionData.customer as CustomerData | null) || null };

  // Expose config globally while preserving ui_config loaded by the lifecycle gate.
  const runtimeConfig =
    window.APP_CONFIG && typeof window.APP_CONFIG === 'object'
      ? { ...(window.APP_CONFIG as Record<string, unknown>) }
      : {};
  window.APP_CONFIG = {
    ...runtimeConfig,
    ...config.value,
  };

  isInitialized.value = true;

}

/**
 * Composable for app configuration
 */
export function useAppConfig() {
  return {
    config: readonly(config),
    isDebug: computed(() => config.value.debug_mode),
    staticUrl: computed(() => config.value.static_host_url),
    assetsPath: computed(() => config.value.restaurant_assets),
  };
}

/**
 * Composable for session data
 */
export function useAppSession() {
  return {
    session: readonly(session),
    customer: computed(() => session.value.customer),
    isLoggedIn: computed(() => !!session.value.customer?.id),
  };
}

export function setAppSessionCustomer(customer: CustomerData | null): void {
  session.value = { customer };
  window.APP_SESSION = { customer };
}

export function getAppSessionCustomer(): CustomerData | null {
  return session.value.customer;
}

/**
 * Composable for API base URL
 */
export function useApiBase() {
  return {
    apiBase: readonly(apiBase),
    apiUrl: computed(() => apiBase.value),
  };
}

/**
 * Get session customer data
 */
export function getSessionCustomer(): CustomerData | null {
  return session.value.customer;
}

/**
 * Get restaurant asset URL
 */
export function getRestaurantAsset(path: string): string {
  const base = config.value.restaurant_assets;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

// Export for direct access
export const appConfig = computed(() => config.value);
export const appSession = computed(() => session.value);
